import React, { useMemo } from 'react';
import { isPositive, trasformStateHappybet } from 'utility/dashboard/functions';

import { DatoCmsMyBetsPage } from 'lib/datoCms/queries/getMyBetsPage';
import { DetailedBetResponseApi } from 'lib/api/detailedBetResponseApi';
import { DetailedBetResponseApi as DetailedBetResponseApiHappybet } from 'sites/happybet/lib/api/detailedBetResponseApi';
import { MieScommesseEvent } from 'components/mieScommesseTableRow/mieScommesseEvent/mieScomesseEvent';
import { MyBet } from 'types/dashboard/myBet';
import { SystemProps } from 'components/detailedBet/DetailedBet';
import { TableField } from 'components/mieScommesseTableRow/tableField/TableField';
import format from 'date-fns/format';
import { getAmount } from 'utility/functions';
import styles from './DetailedBetRow.module.scss';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';

export type DetailedBetRowProps = {
  bet?: MyBet;
  myBetPage?: DatoCmsMyBetsPage;
  detailedResponse: DetailedBetResponseApi | DetailedBetResponseApiHappybet;
};

export const DetailedBetRow = ({ bet, myBetPage, detailedResponse }: DetailedBetRowProps) => {
  const { session } = useInternalSession();
  const labelSport = useGetLabelByKey('sport')!;
  const labelSistema = useGetLabelByKey('sistema')!;
  const labelMultipla = useGetLabelByKey('multipla')!;
  const labelIdBiglietto = useGetLabelByKey('id-biglietto')!;
  const labelConto = useGetLabelByKey('conto')!;
  const labelEmesso = useGetLabelByKey('emesso')!;
  const labelAggiornato = useGetLabelByKey('aggiornato')!;
  const labelImportoGiocato = useGetLabelByKey('importo-giocato')!;
  const labelQuotaTotale = useGetLabelByKey('quota-totale')!;
  const labelPossibileVincita = useGetLabelByKey('possibile-vincita')!;
  const labelCashout = useGetLabelByKey('cashout')!;
  const labelVincita = useGetLabelByKey('vincita')!;
  const labelEventi = useGetLabelByKey('eventi')!;
  const labelEsiti = useGetLabelByKey('esiti')!;
  const labelSingola = useGetLabelByKey('singola');
  const labelDoppia = useGetLabelByKey('doppia');
  const labelPla = useGetLabelByKey('pla');
  const labelMultiple = useGetLabelByKey('multiple');
  const labelPuntata = useGetLabelByKey('puntata');

  const detailedSystem = useMemo(() => {
    let systemData: SystemProps[] = [];
    Array.from({
      length: detailedResponse && detailedResponse.sistema ? detailedResponse?.sistema.numSistemi : 0,
    }).forEach((item, index) => {
      if (detailedResponse.sistema.tipologia[index] === 1) {
        let tmpData = {
          tipologia: labelSingola,
          numColonne: detailedResponse?.sistema.numColonne[index],
          importo: detailedResponse?.sistema.importo[index],
        };
        systemData.push(tmpData);
      } else if (detailedResponse.sistema.tipologia[index] === 2) {
        let tmpData = {
          tipologia: labelDoppia,
          numColonne: detailedResponse?.sistema.numColonne[index],
          importo: detailedResponse?.sistema.importo[index],
        };
        systemData.push(tmpData);
      } else {
        let tmpData = {
          tipologia: `${detailedResponse.sistema.tipologia[index]}-${labelPla}`,
          numColonne: detailedResponse?.sistema.numColonne[index],
          importo: detailedResponse?.sistema.importo[index],
        };
        systemData.push(tmpData);
      }
    });
    return systemData;
  }, [detailedResponse]);

  const formatter = useMemo(() => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      // These options are needed to round to whole numbers if that's what you want.
      //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
      maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
    });
  }, []);

  return (
    <React.Fragment>
      <div className={styles.container}>
        <TableField
          label={myBetPage ? myBetPage.labelImportoGiocato : labelImportoGiocato}
          value={formatter.format(getAmount(bet?.importo))}
        />
        {bet?.tipologia !== 'Sistema' && bet?.tipologia !== 'Totalizzatore' && bet?.tipologia !== 'Nazionale' && (
          <TableField
            label={myBetPage ? myBetPage.labelQuotaTotale : labelQuotaTotale}
            value={
              isPositive(detailedResponse?.quotaTotale)
                ? `${parseFloat(getAmount(detailedResponse?.quotaTotale).toString()).toFixed(2)}`
                : '-'
            }
          />
        )}
        {bet?.tipologia !== 'Totalizzatore' && bet?.tipologia !== 'Nazionale' && (
          <TableField
            label={myBetPage ? myBetPage.labelPossibileVincita : labelPossibileVincita}
            value={isPositive(bet?.vincitaPotenziale) ? formatter.format(getAmount(bet?.vincitaPotenziale)) : '-'}
          />
        )}
        {bet?.eventi && (
          <MieScommesseEvent
            label={bet?.tipologia !== 'Sistema' ? (myBetPage ? myBetPage.labelEventi : labelEventi) : labelEsiti}
            event={{
              win: bet?.eventi?.vintiCount ? bet?.eventi?.vintiCount : 0,
              live: bet?.eventi?.liveCount ? bet?.eventi?.liveCount : 0,
              programmed: bet?.eventi?.programmatiCount ? bet?.eventi?.programmatiCount : 0,
              lose: bet?.eventi?.persiCount ? bet?.eventi?.persiCount : 0,
            }}
          />
        )}
        {trasformStateHappybet(bet?.stato) === 'Chiusa con cash out' && (
          <TableField label={labelCashout} value={formatter.format(getAmount(bet?.importoCashout))} />
        )}
        {trasformStateHappybet(bet?.stato) === 'Vinta' && (
          <TableField label={labelVincita} value={formatter.format(getAmount(bet?.vincita))} />
        )}
      </div>
      <div className={styles.containerArea}>
        <div className={styles.row}>
          <TableField
            classNameData="lowerCase"
            label={myBetPage ? myBetPage.labelIdBiglietto : labelIdBiglietto}
            value={bet?.idBiglietto ?? ''}
          />
          <TableField label={myBetPage ? myBetPage.labelConto : labelConto} value={session?.user?.cardNumber} />
          <TableField
            label={myBetPage ? myBetPage.labelEmesso : labelEmesso}
            value={bet?.dataEmissione ? format(new Date(bet?.dataEmissione), 'dd/MM/yyyy HH:mm') : ''}
          />
          <TableField
            label={myBetPage ? myBetPage.labelAggiornato : labelAggiornato}
            value={bet?.dataModifica ? format(new Date(bet?.dataModifica), 'dd/MM/yyyy HH:mm') : ''}
          />
        </div>
        {detailedSystem.length > 0 && (
          <div className={styles.systemContainer}>
            {detailedSystem.map((item, index) => {
              return (
                <div className={styles.systemGrid} key={index}>
                  <TableField label={labelSistema} value={item.tipologia} />
                  <TableField label={labelMultiple} value={item.numColonne} />
                  <TableField label={labelPuntata} value={formatter.format(getAmount(item.importo))} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};
